import React, { useEffect, useMemo, useState } from "react";
import { axiosSalary } from "../../api/axiosInstance";
import { FaFileExcel } from "react-icons/fa";

const PeridicAttendanceReport = () => {

  /* ================= USER ================= */

  let storedUser = {};

  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const groupId = storedUser?.groupId;

  /* ================= STATE ================= */

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("");

  /* ================= FETCH REPORT ================= */

 /* ================= FETCH REPORT ================= */

const fetchReport = async () => {

  // RESTAURANT REQUIRED

  if (
    !groupId ||
    !selectedRestaurant ||
    !fromDate ||
    !toDate
  ) {
    setData([]);
    return;
  }

  try {

    setLoading(true);

    const res = await axiosSalary.get(
      `/DailyAttendanceReport/DailyReportByRestaurant?groupId=${groupId}&restId=${selectedRestaurant}&fromDate=${fromDate}&toDate=${toDate}`
    );

    setData(res.data || []);

  } catch (err) {

    console.error("Attendance Report Error:", err);

  } finally {

    setLoading(false);
  }
};

  /* ================= FETCH RESTAURANTS ================= */

  const fetchRestaurants = async () => {

    try {

      const res = await axiosSalary.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );

      setRestaurants(res.data || []);

    } catch (err) {

      console.error("Restaurant Fetch Error:", err);
    }
  };

  /* ================= USE EFFECT ================= */

  useEffect(() => {

    if (groupId) {
      fetchRestaurants();
    }

  }, 
  
  [groupId]);

 useEffect(() => {

  if (fromDate && toDate) {

    fetchReport(selectedRestaurant);
  }

}, [fromDate, toDate, selectedRestaurant]);

  /* ================= TOTAL DAYS ================= */

  const totalDaysRange = useMemo(() => {

    if (!fromDate || !toDate) return 0;

    const start = new Date(fromDate);
    const end = new Date(toDate);

    return (
      Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1
    );

  }, [fromDate, toDate]);

  /* ================= FILTER DATA ================= */

  const filteredData = useMemo(() => {

    let temp = [...data];

    // RESTAURANT FILTER

    if (selectedRestaurant) {

      temp = temp.filter(
        (d) => Number(d.restId) === Number(selectedRestaurant)
      );
    }

    // DATE FILTER

    if (fromDate && toDate) {

      temp = temp.filter((d) => {

        const rowDate = d.attendanceDate?.split("T")[0];

        return rowDate >= fromDate && rowDate <= toDate;
      });
    }

    return temp;

  }, [
    data,
    selectedRestaurant,
    fromDate,
    toDate,
  ]);

  /* ================= EXPORT CSV ================= */

  const exportExcel = () => {

  if (!filteredData.length) return;

  let rows = [];

  // GET SELECTED RESTAURANT NAME

  const restaurantName = selectedRestaurant
    ? restaurants.find(
        (r) => Number(r.restId) === Number(selectedRestaurant)
      )?.restaurantName || "Restaurant"
    : "All_Restaurants";

  // GROUP EMPLOYEE WISE

  const grouped = filteredData.reduce((acc, row) => {

    if (!acc[row.empId]) {
      acc[row.empId] = [];
    }

    acc[row.empId].push(row);

    return acc;

  }, {});

  Object.values(grouped).forEach((empRows) => {

    const emp = empRows[0];

    const summaryRow = {

      EmployeeName: emp.empName,
      EmployeeId: emp.empId,

      FromDate: fromDate,
      ToDate: toDate,

      Days: totalDaysRange,

      Present: empRows.filter((r) => r.isPresent).length,

      Absent: empRows.filter((r) => r.isAbsent).length,

      Late: empRows.filter((r) => r.isLate).length,

      WorkingMinutes: empRows.reduce(
        (a, b) => a + (b.workingMinutes || 0),
        0
      ),

      OvertimeMinutes: empRows.reduce(
        (a, b) => a + (b.overtimeMinutes || 0),
        0
      ),
    };

    // SUMMARY TITLE

    rows.push(["EMPLOYEE SUMMARY"]);

    // SUMMARY HEADER

    rows.push(Object.keys(summaryRow));

    // SUMMARY VALUES

    rows.push(Object.values(summaryRow));

    // EMPTY ROW

    rows.push([]);

    // DETAIL HEADER

    rows.push([
      "EmpId",
      "Name",
      "Date",
      "First Punch",
      "Last Punch",
      "WorkingMinutes",
      "Overtime",
      "Status",
    ]);

    // DETAILS

    empRows.forEach((r) => {

   rows.push([
  r.empId,
  r.empName,
  r.attendanceDateDisplay,

  // FIRST PUNCH
  r.firstPunchDisplay
    ? `\t${r.firstPunchDisplay}`
    : "",

  // LAST PUNCH
  r.lastPunchDisplay
    ? `\t${r.lastPunchDisplay}`
    : "",

  r.workingMinutes || 0,
  r.overtimeMinutes || 0,

  r.isAbsent
    ? "Absent"
    : r.isLate
    ? "Late"
    : "Present",
]);
    });

    rows.push([]);
    rows.push([]);
  });

  // CSV

  const csvContent =
    "data:text/csv;charset=utf-8," +
    rows.map((e) => e.join(",")).join("\n");

  const link = document.createElement("a");

  link.href = encodeURI(csvContent);

  // FILE NAME WITH RESTAURANT NAME

  link.download =
    `${restaurantName}_Attendance_Report_${fromDate}_to_${toDate}.csv`;

  document.body.appendChild(link);

  link.click();

  document.body.removeChild(link);
};

  /* ================= GROUP DATA ================= */

  const groupedEmployees = useMemo(() => {

    return Object.values(

      filteredData.reduce((acc, row) => {

        if (!acc[row.empId]) {
          acc[row.empId] = [];
        }

        acc[row.empId].push(row);

        return acc;

      }, {})

    );

  }, [filteredData]);

  /* ================= UI ================= */

  return (

    <div className="bg-white p-5 rounded-2xl shadow border">

      {/* ================= HEADER ================= */}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">

        <h2 className="text-xl font-semibold text-gray-800">
          Periodic Attendance Report
        </h2>

        <div className="flex flex-wrap gap-2">

          {/* FROM DATE */}

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-3 py-2 rounded-lg"
          />

          {/* TO DATE */}

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-3 py-2 rounded-lg"
          />

          {/* RESTAURANT */}

        <select
  value={selectedRestaurant}
  onChange={(e) => {
    setSelectedRestaurant(e.target.value);
  }}
  className="border px-3 py-2 rounded-lg"
>

  <option value="">
    All Restaurants
  </option>

  {restaurants.map((rest) => (

    <option
      key={rest.restId}
      value={rest.restId}
    >
      {rest.restaurantName}
    </option>

  ))}

</select>

          {/* EXPORT */}

          <button
            onClick={exportExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <FaFileExcel />
            Export
          </button>

        </div>
      </div>

      {/* ================= TABLE ================= */}

      <div className="border rounded-xl overflow-auto max-h-[600px]">

        <table className="w-full text-sm">

          <thead className="bg-gray-200 sticky top-0 z-10">

            <tr>

              <th className="p-2 border">
                ID
              </th>

              <th className="p-2 border text-left">
                Name
              </th>

              <th className="p-2 border">
                Date
              </th>

              <th className="p-2 border">
                Working Min
              </th>

              <th className="p-2 border">
                OT Min
              </th>

              <th className="p-2 border">
                Status
              </th>

            </tr>

          </thead>

          <tbody>

            {loading ? (

              <tr>
                <td colSpan="6" className="text-center p-5">
                  Loading...
                </td>
              </tr>

            ) : groupedEmployees.length === 0 ? (

              <tr>
                <td colSpan="6" className="text-center p-5">
                  No Data Found
                </td>
              </tr>

            ) : (

              groupedEmployees.map((empRows, index) => {

                const emp = empRows[0];

                const present = empRows.filter(
                  (r) => r.isPresent
                ).length;

                const absent = empRows.filter(
                  (r) => r.isAbsent
                ).length;

                const late = empRows.filter(
                  (r) => r.isLate
                ).length;

                const workingMinutes = empRows.reduce(
                  (a, b) => a + (b.workingMinutes || 0),
                  0
                );

                const overtimeMinutes = empRows.reduce(
                  (a, b) => a + (b.overtimeMinutes || 0),
                  0
                );

                return (

                  <React.Fragment key={index}>

                    {/* EMPLOYEE SUMMARY */}

                    <tr className="bg-blue-100">

                      <td
                        colSpan="6"
                        className="p-3 font-semibold"
                      >

                        <div className="flex flex-wrap gap-5">

                          <span>
                            👤 {emp.empName}
                          </span>

                          <span>
                            📅 Days: {empRows.length}
                          </span>

                          <span className="text-green-700">
                            ✅ Present: {present}
                          </span>

                          <span className="text-red-700">
                            ❌ Absent: {absent}
                          </span>

                          <span className="text-yellow-700">
                            ⏰ Late: {late}
                          </span>

                          <span>
                            🕒 Work: {workingMinutes}
                          </span>

                          <span>
                            🚀 OT: {overtimeMinutes}
                          </span>

                        </div>

                      </td>

                    </tr>

                    {/* DAILY ROWS */}

                    {empRows.map((row, i) => (

                      <tr
                        key={i}
                        className="hover:bg-blue-50"
                      >

                        <td className="p-2 border text-center">
                          {row.empId}
                        </td>

                        <td className="p-2 border">
                          {row.empName}
                        </td>

                        <td className="p-2 border text-center">
                          {row.attendanceDateDisplay}
                        </td>

                        <td className="p-2 border text-center">
                          {row.workingMinutes || 0}
                        </td>

                        <td className="p-2 border text-center">
                          {row.overtimeMinutes || 0}
                        </td>

                        <td className="p-2 border text-center">

                          {row.isAbsent ? (
                            <span className="text-red-600 font-medium">
                              🔴 Absent
                            </span>
                          ) : row.isLate ? (
                            <span className="text-yellow-600 font-medium">
                              🟡 Late
                            </span>
                          ) : (
                            <span className="text-green-600 font-medium">
                              🟢 Present
                            </span>
                          )}

                        </td>

                      </tr>

                    ))}

                  </React.Fragment>
                );
              })
            )}

          </tbody>

        </table>

      </div>

    </div>
  );
};

export default PeridicAttendanceReport;